import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import moment from 'moment';
import xlsx from 'json-as-xlsx';

import DateRangeSelector from '../../../components/DateRangeSelector';
import { VixieesFlowStep, VixieesLead } from '../models/VixieesLeadModel';
import {
  fetchAllLeads,
  isCapped,
  isLastPage,
  listFlowSteps,
  listLeads,
} from '../services/VixieesLeadsCRUD';

interface LeadsQuery {
  flowStepId: string;
  since?: Date;
  until?: Date;
}

// DRF answers a 400 as {field: [message]}: show its first message, which is
// already translated, and fall back to a generic one for anything else.
function apiErrorMessage(error: any, fallback: string): string {
  const data = error?.response?.data;
  if (data && typeof data === 'object') {
    const first = Object.values(data)[0];
    const message = Array.isArray(first) ? first[0] : first;
    if (typeof message === 'string') return message;
  }
  return fallback;
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function formatCreatedAt(createdAt: string | null): string {
  return createdAt ? moment(createdAt).format('DD/MM/YYYY HH:mm') : '-';
}

export default function VixieesLeadsReport() {
  const intl = useIntl();
  const [flowSteps, setFlowSteps] = useState<VixieesFlowStep[]>([]);
  const [flowStepId, setFlowStepId] = useState<string>('');
  const [since, setSince] = useState<Date>();
  const [until, setUntil] = useState<Date>();
  // DateRangeSelector only reads its initial dates on mount, so clearing them
  // means remounting it.
  const [dateRangeKey, setDateRangeKey] = useState<number>(0);
  // Paging and exporting stick to the last search, not to the form as it is
  // now.
  const [query, setQuery] = useState<LeadsQuery | null>(null);
  const [leads, setLeads] = useState<VixieesLead[] | null>(null);
  const [page, setPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);
  const [exporting, setExporting] = useState<boolean>(false);

  useEffect(() => {
    async function fetchFlowSteps() {
      try {
        const { data } = await listFlowSteps();
        setFlowSteps(data);
      } catch (error) {
        console.warn(error);
        toast.error(
          apiErrorMessage(
            error,
            intl.formatMessage({ id: 'vixieesLeads.flowStepsError' })
          )
        );
      }
    }

    fetchFlowSteps();
  }, [intl]);

  async function loadPage(nextQuery: LeadsQuery, nextPage: number) {
    try {
      setLoading(true);
      const { data } = await listLeads({ ...nextQuery, page: nextPage });
      setQuery(nextQuery);
      setPage(nextPage);
      setLeads(data.results);
    } catch (error) {
      console.warn(error);
      toast.error(
        apiErrorMessage(
          error,
          intl.formatMessage({ id: 'vixieesLeads.fetchError' })
        )
      );
    } finally {
      setLoading(false);
    }
  }

  function onSearch() {
    if (!flowStepId) return;
    loadPage({ flowStepId, since, until }, 1);
  }

  function onClearDates() {
    setSince(undefined);
    setUntil(undefined);
    setDateRangeKey((key) => key + 1);
  }

  async function onExport() {
    if (!query) return;

    try {
      setExporting(true);
      const { leads: allLeads, capped } = await fetchAllLeads(query);
      const t = (id: string) => intl.formatMessage({ id });
      const stepName =
        flowSteps.find((step) => step.id === query.flowStepId)?.name ||
        query.flowStepId;

      xlsx(
        [
          {
            sheet: t('vixieesLeads.exportSheet'),
            columns: [
              { label: t('vixieesLeads.name'), value: 'name' },
              { label: t('vixieesLeads.email'), value: 'email' },
              { label: t('vixieesLeads.phone'), value: 'phone' },
              { label: t('vixieesLeads.createdAt'), value: 'createdAt' },
            ],
            content: allLeads.map((lead) => ({
              name: lead.name,
              email: lead.email,
              phone: lead.phone,
              createdAt: formatCreatedAt(lead.created_at),
            })),
          },
        ] as any,
        {
          fileName: `leads-${slugify(stepName)}-${moment().format(
            'YYYY-MM-DD'
          )}`,
          extraLength: 0,
          writeMode: 'writeFile',
          writeOptions: {},
        }
      );

      if (capped) {
        toast.warn(t('vixieesLeads.capWarning'));
      }
    } catch (error) {
      console.warn(error);
      toast.error(
        apiErrorMessage(
          error,
          intl.formatMessage({ id: 'vixieesLeads.exportError' })
        )
      );
    } finally {
      setExporting(false);
    }
  }

  const busy = loading || exporting;

  return (
    <div>
      <h1 className="mb-8">
        {intl.formatMessage({ id: 'vixieesLeads.title' })}
      </h1>
      <div className="d-flex flex-wrap gap-4 align-items-end">
        <div className="form-group">
          <label htmlFor="flowStep" className="form-label">
            {intl.formatMessage({ id: 'vixieesLeads.stage' })}
          </label>
          <select
            id="flowStep"
            className="form-select"
            value={flowStepId}
            onChange={(e) => setFlowStepId(e.target.value)}
          >
            <option value="">
              {intl.formatMessage({ id: 'vixieesLeads.selectStage' })}
            </option>
            {flowSteps.map((step) => (
              <option key={step.id} value={step.id}>
                {step.name}
              </option>
            ))}
          </select>
        </div>
        <DateRangeSelector
          key={dateRangeKey}
          initialSince={since}
          initialUntil={until}
          label={intl.formatMessage({ id: 'vixieesLeads.dateRange' })}
          onSelected={(selectedSince, selectedUntil) => {
            setSince(selectedSince);
            setUntil(selectedUntil);
          }}
        />
        {(since || until) && (
          <button className="btn btn-light" onClick={onClearDates}>
            {intl.formatMessage({ id: 'vixieesLeads.clearDates' })}
          </button>
        )}
        <button
          className="btn btn-primary"
          onClick={onSearch}
          disabled={busy || !flowStepId}
        >
          {loading && (
            <span className="spinner-border spinner-border-sm me-2" />
          )}
          {intl.formatMessage({ id: 'vixieesLeads.search' })}
        </button>
        <button
          className="btn btn-light-success"
          onClick={onExport}
          disabled={busy || !query || !leads?.length}
        >
          {exporting ? (
            <span className="spinner-border spinner-border-sm me-2" />
          ) : (
            <span className="fas fa-file-excel me-2" />
          )}
          {intl.formatMessage({ id: 'vixieesLeads.export' })}
        </button>
      </div>

      {leads !== null &&
        (leads.length === 0 ? (
          <p className="text-muted mt-8">
            {intl.formatMessage({ id: 'vixieesLeads.empty' })}
          </p>
        ) : (
          <>
            {isCapped(page, leads) && (
              <div className="alert alert-warning mt-8 mb-0">
                {intl.formatMessage({ id: 'vixieesLeads.capWarning' })}
              </div>
            )}
            <div
              className="table-responsive mt-8"
              style={{ maxHeight: 'calc(100vh - 420px)' }}
            >
              {/* Same look as the rest of the CRM tables, striped and with
                  side padding so the zebra rows don't touch the text. */}
              <table className="table table-striped table-hover table-row-dashed table-row-gray-200 align-middle gs-4 gy-4">
                <thead className="table-header sticky-top bg-white">
                  <tr className="text-left fw-bolder">
                    <th>{intl.formatMessage({ id: 'vixieesLeads.name' })}</th>
                    <th>{intl.formatMessage({ id: 'vixieesLeads.email' })}</th>
                    <th>{intl.formatMessage({ id: 'vixieesLeads.phone' })}</th>
                    <th>
                      {intl.formatMessage({ id: 'vixieesLeads.createdAt' })}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead) => (
                    <tr key={lead.id}>
                      <td>{lead.name}</td>
                      <td>{lead.email}</td>
                      <td>{lead.phone}</td>
                      <td>{formatCreatedAt(lead.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ))}

      {query && leads !== null && (page > 1 || leads.length > 0) && (
        <div className="d-flex gap-2 align-items-center justify-content-center mt-4">
          <button
            className="btn btn-light btn-sm"
            disabled={busy || page <= 1}
            onClick={() => loadPage(query, page - 1)}
          >
            <span className="fa fa-chevron-left"></span>
          </button>
          <span>
            {intl.formatMessage({ id: 'vixieesLeads.page' }, { page })}
          </span>
          <button
            className="btn btn-light btn-sm"
            disabled={busy || isLastPage(page, leads)}
            onClick={() => loadPage(query, page + 1)}
          >
            <span className="fa fa-chevron-right"></span>
          </button>
        </div>
      )}
    </div>
  );
}
